import { useMutation } from '@tanstack/react-query';

import { ethers } from 'ethers';
import { useDisconnect } from 'wagmi';

import { SNAPSHOT } from '@shared/config/snapshot';
import validateMatchingAddresses from '@shared/helpers/web3/validateMatchingAddresses';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

import { useSnapshot } from '@store:web/components/providers/SnapshotProvider';
import type {
  StoredSnapshotVoteParams} from '@store:web/utils/hooks/useSnapshotStoredVote';
import {
  storeSnapshotVote,
} from '@store:web/utils/hooks/useSnapshotStoredVote';

export const useSnapshotCastVoteMutation = () => {
  const { userWallet: connectedWallet } = useMagicIdWallet();
  const snapshot = useSnapshot();
  const { disconnectAsync } = useDisconnect();

  return useMutation({
    mutationFn: async ({
      proposalId,
      choice,
      appId,
      reason,
    }: Pick<StoredSnapshotVoteParams, 'proposalId' | 'appId' | 'choice' | 'reason'>) => {
      if (!connectedWallet) {
        throw new Error('Wallet not connected to the store.');
      }

      const { space = '' } = SNAPSHOT;
      const evm = await import('@shared/helpers/web3/evm').then((module) => module.default);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const web3 = new ethers.providers.LegacyWeb3Provider(evm.getProvider());
      const [account] = await web3.listAccounts();

      if (!account) {
        throw new Error('Account not provided');
      }

      await validateMatchingAddresses({
        userAddress: account,
        targetAddress: connectedWallet.attributes.address,
        disconnectAsync,
      });

      const receipt = await snapshot.vote(web3, account, {
        space,
        proposal: proposalId,
        type: 'single-choice',
        choice,
        reason,
        app: appId,
      });

      storeSnapshotVote({
        appId,
        proposalId,
        account,
        choice,
      });

      return receipt;
    },
  });
};
